<template>
  <div
    :style="containerStyle"
    :class="{show: showMenu}"
    class="appsumo-dropdown-outer-container"
  >
    <button
      :class="{show: showMenu}"
      :disabled="disabled"
      :style="buttonStyle"
      data-testid="sort-dropdown"
      aria-haspopup="true"
      class="frontend-dropdown no-outline"
      @click="toggleMenu()"
    >
      <slot />
    </button>
    <div
      ref="dropdownMenu"
      class="appsumo-dropdown-inner-container"
      :class="[
        { show: showMenu },
        { above: isAbove },
      ]"
    >
      <template v-for="(option, index) in options">
        <NuxtLink
          v-if="option.url"
          :key="`link-${index}`"
          :to="option.url"
          :class="{
            'highlight': (highlight && option.value === value),
            'disabled': !!option.disabled
          }"
          class="frontend-dropdown dropdown-item"
          :value="option.value"
        >
          <img
            v-if="option.img"
            class="mr-1"
            :src="option.img"
            :alt="`${option.display} Icon`"
            width="15"
            height="15"
          >
          <span v-html="getDisplayHtml(option)" />
          <span v-if="option.subText" class="subtext">{{ option.subText }}</span>
          <span v-else-if="option.isTaco">
            <DropdownElementTaco :data="option" />
          </span>
        </NuxtLink>
        <a
          v-else-if="option.externalUrl"
          :key="`link-external-${index}`"
          :href="option.externalUrl"
          :class="{
            'highlight': (highlight && option.value === value),
            'disabled': !!option.disabled
          }"
          class="frontend-dropdown dropdown-item"
          :value="option.value"
        >
          <img
            v-if="option.img"
            class="mr-1"
            :src="option.img"
            :alt="`${option.display} Icon`"
            width="15"
            height="15"
          >
          <span v-html="getDisplayHtml(option)" />
          <span v-if="option.subText" class="subtext">{{ option.subText }}</span>
          <span v-else-if="option.isTaco">
            <DropdownElementTaco :data="option" />
          </span>
        </a>
        <div
          v-else
          :key="index"
          :value="option.value"
          :class="{
            'highlight': (highlight && option.value === value),
            'disabled': !!option.disabled
          }"
          class="frontend-dropdown dropdown-item"
          @click="onClickOption(option)"
        >
          <img
            v-if="option.img"
            class="mr-1"
            :src="option.img"
            :alt="`${option.display} Icon`"
            width="15"
            height="15"
          >
          <span v-html="getDisplayHtml(option)" />
          <span v-if="option.subText" class="subtext">{{ option.subText }}</span>
          <span v-else-if="option.isTaco">
            <DropdownElementTaco :data="option" />
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import DropdownElementTaco from './DropdownElementTaco.vue';

// 5px to give the dropdown some breathing room
const PX_BUFFER = 5;

export default {
  name: 'LegacyDropdown',
  components: {
    DropdownElementTaco,
  },
  props: {
    containerStyle: {
      type: String,
      default: '',
    },
    containerClass: {
      type: String,
      default: '',
    },
    buttonStyle: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
    },
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [String, Number],
      default: '',
    },
    highlight: {
      type: Boolean, // Highlight currently selected item
    },
  },
  data() {
    return {
      showMenu: false,
      isAbove: false,
    };
  },
  mounted() {
    this.handleDebouncedScroll = debounce(this.onScroll, 100);
    window.addEventListener('scroll', this.handleDebouncedScroll);
  },
  destroyed() {
    window.removeEventListener('click', this.clickHandler);
    window.removeEventListener('scroll', this.handleDebouncedScroll);
  },
  methods: {
    getDisplayHtml(option = {}) {
      if (option.displayArray) {
        // even with a purgecss ignore, our 'text-brick' class gets purged?
        // I've manually styled the dorado 'brick' color below to accommodate
        return `
          <span>
            ${option.displayArray[0]}:
          </span>
          <span style="color: #c22f0f;">
            ${option.displayArray[1]}
          </span>
        `;
      }

      if (option.display) {
        return `${option.display}`;
      }

      return '';
    },
    toggleMenu() {
      if (!this.showMenu) { // No need to calculate if closing dropdown
        this.calculatePosition();
      }

      this.showMenu = !this.showMenu;
      this.$emit('toggle', this.showMenu);

      if (this.showMenu) {
        window.addEventListener('click', this.clickHandler);
      }
    },
    calculatePosition() {
      // Check if need to render above menu
      const { $el: parent } = this;
      const { top: parentTop, bottom: parentBottom } = parent.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const menuHeight = this.$refs.dropdownMenu.clientHeight;

      const topPos = parentTop - menuHeight - PX_BUFFER;
      const bottomPos = parentBottom + menuHeight + PX_BUFFER;
      const bottomOverage = windowHeight - bottomPos;

      // Check if can render on bottom (default)
      // Add check that top position will show better (if both will render offscreen)
      if (bottomPos > windowHeight && topPos > bottomOverage) {
        this.isAbove = true;
      } else if (this.isAbove) {
        this.isAbove = false;
      }
    },
    clickHandler(event) {
      const { target } = event;
      const { $el } = this;

      if (!$el.contains(target)) {
        this.showMenu = false;
        this.$emit('toggle', this.showMenu);

        window.removeEventListener('click', this.clickHandler);
      }
    },
    onScroll() {
      // Check if scrolling will require dropdown to flip
      if (this.showMenu) this.calculatePosition();
    },
    onClickOption(value) {
      if (value.disabled) {
        return;
      }

      this.$emit('selected', value);
      this.toggleMenu();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/utils/breakpoints';
  /* stylelint-disable selector-no-qualifying-type */
  .appsumo-dropdown-outer-container {
    position: relative;
    overflow: hidden;
    padding: 1px; // prevents dropdown borders from being obscured in various browser sizes

    &.show {
      overflow: initial;
    }

    button {
      text-align: left;
      border: 2px solid #eee;
      background-color: #fff;
      font-weight: 600;
      border-radius: 3px;
      font-size: 12px;
      line-height: 1.4;
      padding: 14px 30px 14px 10px;
      width: 100%;
      white-space: nowrap;
      transition:
        color 0.15s ease-in-out,
        background-color 0.15s ease-in-out,
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;

      &.show::after {
        transform: scaleY(-1);
      }

      &::after {
        opacity: 1;
        background-image: url(../../../static/images/svg/dropdown-arrow.svg) !important;
        background-repeat: no-repeat;
        background-position: center right;
        border: 0;
        width: 15px;
        height: 100%;
        top: 0;
        bottom: 0;
        right: 12px;
        z-index: 1000;
        display: inline-block;
        content: '';
        position: absolute;
        margin-left: 0.3em;
        color: #0275d8;
        transition: transform 0.2s ease-in-out;
      }
    }

    &.small > button {
      padding: 10px 30px 10px 10px;
    }

    .appsumo-dropdown-inner-container {
      opacity: 0;
      transition: opacity 125ms, z-index 125ms;
      position: absolute;
      left: 0;
      top: 100%;
      background-color: #fff;
      z-index: -1000;
      border-radius: 0 0 3px 3px;
      border: 1px solid #ccc;
      margin-top: 1px;
      width: 100%;

      &.show {
        opacity: 1;
        // jl: in the case that two dropdowns are stacked on top of each other & the user clicks on
        // the top dropdown, the caret from the bottom dropdown will peek thru the top's inner
        // container. this z-index temporarily solves that problem.
        z-index: 1001;
      }

      &.above {
        top: initial;
        bottom: 100%;
        border-radius: 3px 3px 0 0;
      }

      > .dropdown-item {
        display: flex;
        gap: 3px;
        padding: 9px 10px;
        font-size: 12px;
        line-height: 1.4;
        width: 100%;
        color: #212529;
        text-align: inherit;
        white-space: nowrap;
        background-color: transparent;
        border: 0;
        cursor: pointer;

        &:hover,
        &.highlight {
          transition: all 0.1s linear;
          background-color: rgba(2, 117, 216, 0.8);
          border: 0;
          color: #fff;
        }

        &.disabled {
          color: #b2b2b2 !important;
          cursor: default;
        }

        &.disabled img {
          opacity: 0.5;
        }
      }
    }
  }

  .no-outline {
    outline: none;
  }

  span.subtext {
    color: #434d59;
    font-size: 16px;
  }

  .pricing-table.appsumo-dropdown-outer-container {
    button {
      background-color: #0275d8;
      border-color: #0275d8 !important;
      color: #fff !important;
      padding: 8px 30px 8px 10px;

      &::after {
        background-image: url(../../../static/images/svg/bottom-chevron_white.svg) !important;
      }

      .appsumo-dropdown-inner-container > div {
        &:hover,
        &.highlight {
          color: #fff !important;
        }
      }
    }
  }

  .pricing-card.appsumo-dropdown-outer-container {
    button.frontend-dropdown,
    div.frontend-dropdown {
      padding: 12px 18px !important;
      font-weight: 400 !important;
      font-size: 16px !important;
      line-height: 24px !important;
      color: #1b1b1b !important;
    }

    button.frontend-dropdown {
      border: 1px solid #434d59 !important;
    }

    button.frontend-dropdown::after,
    button.frontend-dropdown::before {
      right: 18px !important;
    }
  }

  @include media-breakpoint-down(sm) {
    .appsumo-dropdown-outer-container {
      width: 100%;

      .appsumo-dropdown-inner-container {
        width: 100%;
      }
    }
  }
  /* stylelint-enable selector-no-qualifying-type */
</style>
